/**
 * KLineRenderer — TradingView Lightweight Charts K线蜡烛图渲染器
 *
 * 将 ```kline 代码块渲染为专业的金融蜡烛图。
 * 支持：时间轴缩放、十字准线悬停、深浅自适应。
 *
 * AI 输出 JSON 格式：
 * {
 *   "title": "某公司股价走势",
 *   "type": "candlestick" | "line" | "area" | "histogram",
 *   "data": [
 *     {"time": "2023-01-01", "open": 100, "high": 120, "low": 90, "close": 110, "volume": 5000},
 *     ...
 *   ]
 * }
 */

import React, { useEffect, useRef, useState, useMemo, useId } from 'react';
import { createChart, ColorType, CandlestickSeries, LineSeries, AreaSeries, HistogramSeries } from 'lightweight-charts';
import type { IChartApi } from 'lightweight-charts';

// Institutional Minimalist 配色
const COLORS = {
    background: '#FAFAFA',
    textColor: '#374151',
    lineColor: '#E5E7EB',
    upColor: '#10B981',      // 涨 (绿 — 国际惯例)
    downColor: '#EF4444',    // 跌 (红)
    lineSeriesColor: '#3B82F6', // Blue 500
    areaTopColor: 'rgba(59, 130, 246, 0.4)',
    areaBottomColor: 'rgba(59, 130, 246, 0.04)',
    volumeUp: 'rgba(16, 185, 129, 0.5)',
    volumeDown: 'rgba(239, 68, 68, 0.5)',
};

interface KLineRendererProps {
    optionString: string;
}

const KLineRenderer: React.FC<KLineRendererProps> = ({ optionString }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<IChartApi | null>(null);
    const [error, setError] = useState<string>('');
    const uniqueId = useId().replace(/:/g, '_');

    const config = useMemo(() => {
        try {
            return JSON.parse(optionString.trim());
        } catch (err: any) {
            setError(err.message);
            return null;
        }
    }, [optionString]);

    useEffect(() => {
        if (!config || !containerRef.current) return;

        try {
            const chart = createChart(containerRef.current, {
                layout: {
                    background: { type: ColorType.Solid, color: COLORS.background },
                    textColor: COLORS.textColor,
                    fontFamily: 'Inter, "Noto Sans SC", sans-serif',
                },
                grid: {
                    vertLines: { color: COLORS.lineColor },
                    horzLines: { color: COLORS.lineColor },
                },
                width: containerRef.current.clientWidth,
                height: 360,
                crosshair: {
                    mode: 0, // Normal
                },
                timeScale: {
                    borderColor: COLORS.lineColor,
                    timeVisible: true,
                },
                rightPriceScale: {
                    borderColor: COLORS.lineColor,
                },
            });

            chartRef.current = chart;

            const chartType = config.type || 'candlestick';
            const data = config.data || [];

            if (chartType === 'candlestick') {
                const series = chart.addSeries(CandlestickSeries, {
                    upColor: COLORS.upColor,
                    downColor: COLORS.downColor,
                    borderDownColor: COLORS.downColor,
                    borderUpColor: COLORS.upColor,
                    wickDownColor: COLORS.downColor,
                    wickUpColor: COLORS.upColor,
                });
                series.setData(data);
            } else if (chartType === 'line') {
                const series = chart.addSeries(LineSeries, {
                    color: COLORS.lineSeriesColor,
                    lineWidth: 2,
                });
                const lineData = data.map((d: any) => ({ time: d.time, value: d.close ?? d.value ?? d.price }));
                series.setData(lineData);
            } else if (chartType === 'area') {
                const series = chart.addSeries(AreaSeries, {
                    lineColor: COLORS.lineSeriesColor,
                    topColor: COLORS.areaTopColor,
                    bottomColor: COLORS.areaBottomColor,
                    lineWidth: 2,
                });
                const areaData = data.map((d: any) => ({ time: d.time, value: d.close ?? d.value ?? d.price }));
                series.setData(areaData);
            } else if (chartType === 'histogram') {
                const series = chart.addSeries(HistogramSeries, {
                    color: COLORS.lineSeriesColor,
                });
                const histData = data.map((d: any) => ({ time: d.time, value: d.volume ?? d.value }));
                series.setData(histData);
            }

            chart.timeScale().fitContent();

            // Resize observer
            const ro = new ResizeObserver(() => {
                if (containerRef.current) {
                    chart.applyOptions({ width: containerRef.current.clientWidth });
                }
            });
            ro.observe(containerRef.current);

            return () => {
                ro.disconnect();
                chart.remove();
                chartRef.current = null;
            };
        } catch (err: any) {
            setError(err.message);
        }
    }, [config]);

    if (error) {
        return (
            <div style={{
                margin: '12px 0', padding: '16px', background: '#F9FAFB',
                border: '1px solid #E5E7EB', borderRadius: '8px', position: 'relative',
            }}>
                <div style={{ position: 'absolute', top: '8px', right: '12px', fontSize: '11px', color: '#9CA3AF', fontWeight: 600 }}>
                    K-LINE
                </div>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '13px', color: '#374151', fontFamily: 'monospace' }}>
                    {optionString}
                </pre>
            </div>
        );
    }

    return (
        <div style={{
            margin: '16px 0', border: '1px solid #E5E7EB', borderRadius: '12px',
            overflow: 'hidden', background: COLORS.background, position: 'relative',
        }}>
            {/* 标题 */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', borderBottom: '1px solid #E5E7EB',
            }}>
                <div style={{ fontSize: '13px', color: '#1F2937', fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                    {config?.title || '金融图表'}
                </div>
                <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#F59E0B', display: 'inline-block' }} />
                    K-LINE
                </div>
            </div>
            {/* Chart Container */}
            <div id={`kline-${uniqueId}`} ref={containerRef} style={{ width: '100%' }} />
        </div>
    );
};

export default KLineRenderer;
